import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    AlertCircle, ArrowLeft, Plus, Trash2, Save, CheckCircle2, XCircle,
    UploadCloud, RotateCcw, FlaskConical, CircleDot, FileCode2, Download, FolderUp,
} from 'lucide-react';
import JSZip from 'jszip';
import uapiIntegrationsService from '../services/UapiIntegrationsService';
import UapiIntegrationAssistant from './UapiIntegrationAssistant';
import Button from './shared/Button';
import VersionsTable from './shared/VersionsTable';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import { formatDateTime } from '../utils/datetime';
import './UapiIntegrations.css';

// One integration: its file tree, its versions, and the properties pinned to a candidate.
//
// THE DRAFT IS WHAT YOU EDIT. Published versions are immutable — the server refuses a write against one
// — so the editor always works on the draft and a published version is opened READ-ONLY, to read or to
// restore from. That is the whole version model: draft → publish (snapshot) → make live (pointer).
//
// PUBLISH IS NOT GO-LIVE, and the two buttons are deliberately far apart. Publishing freezes what you
// wrote; making it live changes what every property on this system runs. Splitting them is what lets a
// version be tried on one x-hotelid first, and what makes a rollback a pointer move rather than an edit
// under an outage.
//
// A PLAIN TEXTAREA, not a syntax-highlighting editor. The portal has no editor dependency (AthenaEditor
// is a textarea too), and adding one for this screen would put a large package in the bundle for every
// user of the portal to serve a handful of superadmins. Monospace, tabs that indent, and the compile
// check one button away covers what the author actually needs — which is to know the tree LOADS, and
// that is a question no highlighter can answer.

// yyyy-mm-dd-hh-mm, so exports of the same tree sort by when they were taken.
const stamp = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}-${p(d.getMinutes())}`;
};

// Zipping a FOLDER puts every file under it, so a zip that came from a checkout arrives as
// `my-integration/index.js` and nothing matches the entry point. Drop the prefix when every entry
// shares one — the alternative is a confusing refusal for the most ordinary way to make a zip.
const stripCommonRoot = (paths) => {
    if (paths.length < 2) return '';
    const root = paths[0].split('/')[0];
    if (!root || !paths.every(p => p.startsWith(`${root}/`))) return '';
    return `${root}/`;
};

// Editor noise and OS junk. These are never part of an integration and their presence in a zip is an
// accident of how it was made, not a decision by the author.
const IGNORED = (path) => path.startsWith('__MACOSX/') || path.split('/').some(seg =>
    seg === '.DS_Store' || seg === 'Thumbs.db' || seg === '.git' || seg === 'node_modules');

function UapiIntegrationDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [integration, setIntegration] = useState(null);
    const [versions, setVersions] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [pinnable, setPinnable] = useState([]);
    const [files, setFiles] = useState([]);
    const [openPath, setOpenPath] = useState(null);
    const [content, setContent] = useState('');
    const [dirty, setDirty] = useState(false);
    const [viewingVersion, setViewingVersion] = useState(null);   // null = the draft
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [check, setCheck] = useState(null);                      // { ok, error } from /validate
    const [dialog, setDialog] = useState(null);                    // { kind, … }
    const [newPath, setNewPath] = useState('');
    const [pinForm, setPinForm] = useState({ uapi_access_id: '', version_id: '', notes: '' });
    const textarea = useRef(null);
    const zipInput = useRef(null);

    const readOnly = viewingVersion !== null;

    const loadIntegration = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await uapiIntegrationsService.get(id);
            const d = res?.data || res || {};
            setIntegration(d.integration || null);
            setVersions(Array.isArray(d.versions) ? d.versions : []);
            setCandidates(Array.isArray(d.candidates) ? d.candidates : []);
            setPinnable(Array.isArray(d.pinnable) ? d.pinnable : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The integration could not be loaded');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const loadFiles = useCallback(async (versionId) => {
        try {
            const res = await uapiIntegrationsService.listFiles(id, versionId || undefined);
            const d = res?.data || res || {};
            const list = Array.isArray(d.files) ? d.files : [];
            setFiles(list);
            return list;
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The files could not be loaded');
            return [];
        }
    }, [id]);

    useEffect(() => { loadIntegration(); }, [loadIntegration]);

    const openFile = async (path, versionId = viewingVersion) => {
        try {
            const res = await uapiIntegrationsService.getFile(id, path, versionId || undefined);
            const f = res?.data || res || {};
            setOpenPath(path);
            setContent(f.content || '');
            setDirty(false);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'That file could not be opened');
        }
    };


    // Open the entry point first. It is the file the loader starts from, so it is the one that explains
    // the integration — and opening on an empty pane makes the screen look like it failed to load.
    useEffect(() => {
        if (!integration) return;
        (async () => {
            const list = await loadFiles(viewingVersion);
            const first = list.find(f => f.path === integration.entry_path) || list[0];
            if (first) openFile(first.path, viewingVersion);
            else { setOpenPath(null); setContent(''); }
        })();
        // Deliberately keyed on the integration id and the version being viewed, NOT on loadFiles or
        // openFile: this fires when the SUBJECT changes, and adding the callbacks would re-open the
        // entry point every time an unrelated piece of state re-created them, discarding whatever file
        // the author had open.
    }, [integration?.id, viewingVersion]);

    const save = async () => {
        if (!openPath || readOnly) return;
        try {
            setBusy(true);
            await uapiIntegrationsService.saveFile(id, openPath, content);
            setDirty(false);
            setCheck(null);          // the tree changed; the last compile result no longer describes it
            await loadFiles(null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The file could not be saved');
        } finally {
            setBusy(false);
        }
    };

    // Compile with the loader the DISPATCHER runs. `ok:false` is an answer, not a failure — it is shown
    // beside the editor rather than as an error banner.
    const validate = async () => {
        try {
            setBusy(true);
            const res = await uapiIntegrationsService.validate(id, viewingVersion || undefined);
            setCheck(res?.data || res || null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The check could not be run');
        } finally {
            setBusy(false);
        }
    };

    // ── zip export / import ──────────────────────────────────────────────────
    // Both ends of the zip are handled HERE, in the browser: jszip is already in this bundle, and the
    // server exchanges the tree as { path: content } either way. Exporting a published version is
    // allowed and useful — it is how you get a copy of what is actually live.

    const downloadZip = async () => {
        try {
            setBusy(true);
            setError(null);
            const res = await uapiIntegrationsService.getTree(id, viewingVersion || undefined);
            const tree = (res?.data || res || {}).files || {};
            const paths = Object.keys(tree);
            if (!paths.length) { setError('There is nothing to download — this tree has no files'); return; }
            const zip = new JSZip();
            for (const path of paths) zip.file(path, tree[path]);
            const blob = await zip.generateAsync({ type: 'blob' });
            const which = viewingVersion
                ? `v${versions.find(v => v.id === viewingVersion)?.version ?? viewingVersion}`
                : 'draft';
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${integration.system_name || integration.name}-${which}-${stamp()}.zip`.replace(/\s+/g, '-');
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The zip could not be built');
        } finally {
            setBusy(false);
        }
    };

    // Unpack and CHECK, then hand the result to a confirm dialog. Nothing is written here: a replace
    // deletes every file the zip does not contain, so the author sees what is about to land — how many
    // files, and which of the current ones disappear — before agreeing to it.
    const readZip = async (file) => {
        if (!file) return;
        try {
            setBusy(true);
            setError(null);
            const zip = await JSZip.loadAsync(file);
            const entries = [];
            zip.forEach((rel, entry) => { if (!entry.dir && !IGNORED(rel)) entries.push(entry); });
            if (!entries.length) { setError('That zip contains no files'); return; }

            const prefix = stripCommonRoot(entries.map(e => e.name));
            const tree = {};
            for (const entry of entries) {
                const path = prefix && entry.name.startsWith(prefix) ? entry.name.slice(prefix.length) : entry.name;
                const content = await entry.async('string');
                // A NUL byte means this was never text. Read as a string it would import as mangled
                // characters and fail to compile with an error pointing at the wrong thing entirely.
                if (content.includes('\u0000')) {
                    setError(`${path} is a binary file — an integration tree holds source files only`);
                    return;
                }
                tree[path] = content;
            }
            setDialog({
                kind: 'importzip',
                tree,
                name: file.name,
                removed: files.map(f => f.path).filter(p => !Object.prototype.hasOwnProperty.call(tree, p)),
            });
        } catch (err) {
            setError(err?.message ? `That zip could not be read: ${err.message}` : 'That zip could not be read');
        } finally {
            setBusy(false);
        }
    };

    const run = async (fn, after) => {
        try {
            setBusy(true);
            setError(null);
            await fn();
            setDialog(null);
            if (after) await after();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'That did not work');
            setDialog(null);
        } finally {
            setBusy(false);
        }
    };

    // Tab indents instead of leaving the textarea. In a code pane, tab-to-next-control is never what the
    // author meant, and losing the caret mid-function is worse than the accessibility cost here — every
    // other control on the screen is still reachable.
    const onKeyDown = (e) => {
        if (e.key !== 'Tab') return;
        e.preventDefault();
        const el = e.target;
        const { selectionStart: s, selectionEnd: t } = el;
        const next = `${content.slice(0, s)}    ${content.slice(t)}`;
        setContent(next);
        setDirty(true);
        requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 4; });
    };

    if (loading) return <div className="uai-page"><div className="uai-loading"><Spinner /> Loading…</div></div>;
    if (!integration) {
        return (
            <div className="uai-page">
                <div className="uai-banner uai-banner-error"><AlertCircle size={16} /> <span>{error || 'Not found'}</span></div>
            </div>
        );
    }

    const liveVersion = versions.find(v => v.id === integration.live_version_id);

    return (
        <div className="uai-page">
            <div className="uai-header">
                <div>
                    <button className="uai-back" onClick={() => navigate('/admin/universal-api/integrations')}>
                        <ArrowLeft size={15} /> Integrations
                    </button>
                    <h1 className="uai-title">{integration.name}</h1>
                    <p className="uai-sub">
                        {integration.system_name} · entry point <code>{integration.entry_path}</code>
                        {liveVersion
                            ? <> · <span className="uai-badge uai-badge-live"><CircleDot size={12} /> v{liveVersion.version} live</span></>
                            // With no version live, a system with a working built-in adapter is still served by it.
                            : integration.builtin_adapter
                                ? <> · <span className="uai-badge uai-badge-idle" title={`Calls are served by ${integration.builtin_adapter} until a version is live`}>nothing live — served by the built-in adapter</span></>
                                : <> · <span className="uai-badge uai-badge-idle">nothing live — every call answers 501</span></>}
                    </p>
                </div>
                <div className="uai-header-actions">
                    <Button variant="secondary" onClick={validate} disabled={busy}>
                        <CheckCircle2 size={14} /> Check
                    </Button>
                    <Button onClick={() => setDialog({ kind: 'publish', notes: '' })} disabled={busy || readOnly}>
                        <UploadCloud size={14} /> Publish draft
                    </Button>
                </div>
            </div>

            {error && <div className="uai-banner uai-banner-error"><AlertCircle size={16} /> <span>{error}</span></div>}

            {check && (
                <div className={`uai-banner ${check.ok ? 'uai-banner-ok' : 'uai-banner-error'}`}>
                    {check.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    <span>
                        {check.ok
                            ? `Compiles — ${check.files} file${check.files === 1 ? '' : 's'}, entry point ${integration.entry_path}`
                            : check.error}
                    </span>
                </div>
            )}

            {/* Which tree is on screen. A published version is opened to READ, never to edit — the
                server refuses the write anyway, and a pane that accepts keystrokes it will not save is
                the worst way to learn that. */}
            <div className="uai-versionbar">
                <span className="uai-label-inline">Showing</span>
                <select
                    className="uai-input uai-input-inline"
                    value={viewingVersion || ''}
                    onChange={(e) => { setViewingVersion(e.target.value ? Number(e.target.value) : null); setCheck(null); }}
                >
                    <option value="">Draft (editable)</option>
                    {versions.filter(v => v.version > 0).map(v => (
                        <option key={v.id} value={v.id}>
                            v{v.version}{v.id === integration.live_version_id ? ' — live' : ''} · {formatDateTime(v.published_at)}
                        </option>
                    ))}
                </select>
                {readOnly && (
                    <>
                        <span className="uai-readonly">read-only</span>
                        <Button variant="secondary" onClick={() => setDialog({ kind: 'restore' })} disabled={busy}>
                            <RotateCcw size={14} /> Restore into draft
                        </Button>
                    </>
                )}
            </div>

            <div className="uai-split">
                <aside className="uai-tree">
                    <div className="uai-tree-head">
                        <span>Files</span>
                        {/* Download works on whatever tree is on screen, a published version included —
                            that is how you take a copy of what is actually live. Upload writes the
                            DRAFT and is hidden while a published version is open, because the server
                            refuses that write and offering it would only teach you so the hard way. */}
                        <div className="uai-tree-actions">
                            <button className="uai-icon-btn" title="Download as zip"
                                onClick={downloadZip} disabled={busy || files.length === 0}>
                                <Download size={14} />
                            </button>
                            {!readOnly && (
                                <>
                                    <button className="uai-icon-btn" title="Upload a zip into the draft"
                                        onClick={() => zipInput.current && zipInput.current.click()} disabled={busy}>
                                        <FolderUp size={14} />
                                    </button>
                                    <input
                                        ref={zipInput}
                                        type="file"
                                        accept=".zip,application/zip"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const f = e.target.files && e.target.files[0];
                                            // Cleared before reading so picking the SAME file twice fires
                                            // change again — after a failed import that is exactly what
                                            // someone does next.
                                            e.target.value = '';
                                            readZip(f);
                                        }}
                                    />
                                    <button className="uai-icon-btn" title="New file" onClick={() => setDialog({ kind: 'newfile' })}>
                                        <Plus size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    {files.length === 0 && <div className="uai-tree-empty">No files</div>}
                    {files.map(f => (
                        <div
                            key={f.path}
                            className={`uai-tree-row${f.path === openPath ? ' uai-tree-row-open' : ''}`}
                            onClick={() => openFile(f.path)}
                        >
                            <FileCode2 size={13} />
                            <span className="uai-tree-path">{f.path}</span>
                            {f.path === integration.entry_path && <span className="uai-entry-tag">entry</span>}
                            {!readOnly && f.path !== integration.entry_path && (
                                <button
                                    className="uai-icon-btn uai-icon-danger"
                                    title="Delete file"
                                    onClick={(e) => { e.stopPropagation(); setDialog({ kind: 'delfile', path: f.path }); }}
                                >
                                    <Trash2 size={13} />
                                </button>
                            )}
                        </div>
                    ))}
                </aside>

                <section className="uai-editor">
                    <div className="uai-editor-head">
                        <code>{openPath || 'no file open'}</code>
                        {dirty && <span className="uai-dirty">unsaved</span>}
                        {!readOnly && (
                            <Button onClick={save} disabled={busy || !dirty || !openPath}>
                                <Save size={14} /> Save
                            </Button>
                        )}
                    </div>
                    <textarea
                        ref={textarea}
                        className="uai-code"
                        value={content}
                        readOnly={readOnly}
                        spellCheck={false}
                        onKeyDown={onKeyDown}
                        onChange={(e) => { setContent(e.target.value); setDirty(true); }}
                        placeholder={openPath ? '' : 'Select a file'}
                    />
                </section>
            </div>

            {/* The assistant, directly under the editor it writes into: a file it proposes is applied
                to the draft, and the tree and the open file reload underneath so what is on screen is
                what was written — never the version before it. */}
            <UapiIntegrationAssistant
                integrationId={id}
                marketplaceId={integration.marketplace_id}
                readOnly={readOnly}
                onApplied={async (path) => {
                    await loadFiles(null);
                    setCheck(null);       // the tree changed; the last compile result no longer describes it
                    if (!readOnly && (path === openPath || !openPath)) await openFile(path, null);
                }}
            />

            {/* Candidates: one x-hotelid runs a version the rest of the world does not. This is how a
                version is tried against the real backend before it is made live. */}
            <div className="uai-card">
                <div className="uai-card-head">
                    <h2 className="uai-card-title"><FlaskConical size={15} /> Candidate pins</h2>
                    <span className="uai-hint">
                        One property runs a chosen version — including the draft — while everyone else stays on live.
                    </span>
                </div>

                {candidates.length === 0
                    ? <p className="uai-empty-inline">Nothing pinned. Every x-hotelid on this system runs the live version.</p>
                    : (
                        <table className="uai-table">
                            <thead>
                                <tr><th>Property</th><th>x-hotelid</th><th>Version</th><th>Expires</th><th>Note</th><th /></tr>
                            </thead>
                            <tbody>
                                {candidates.map(c => (
                                    <tr key={c.id}>
                                        <td>{c.hotelname || `hotel ${c.hotel_id}`}</td>
                                        <td><code className="uai-syskey">{c.uapi_hotel_id}</code></td>
                                        <td>{c.version === 0 ? 'draft' : `v${c.version}`}</td>
                                        <td className="uai-muted">{c.expires_at ? formatDateTime(c.expires_at) : 'no expiry'}</td>
                                        <td className="uai-muted">{c.notes || '—'}</td>
                                        <td>
                                            <button className="uai-icon-btn uai-icon-danger" title="Unpin"
                                                onClick={() => setDialog({ kind: 'unpin', candidate: c })}>
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                <div className="uai-pinform">
                    <select className="uai-input uai-input-inline" value={pinForm.uapi_access_id}
                        onChange={(e) => setPinForm(p => ({ ...p, uapi_access_id: e.target.value }))}>
                        <option value="">Property…</option>
                        {pinnable.map(a => (
                            <option key={a.uapi_access_id} value={a.uapi_access_id}>
                                {a.hotelname || `hotel ${a.hotel_id}`} — {a.uapi_hotel_id}
                            </option>
                        ))}
                    </select>
                    <select className="uai-input uai-input-inline" value={pinForm.version_id}
                        onChange={(e) => setPinForm(p => ({ ...p, version_id: e.target.value }))}>
                        <option value="">Version…</option>
                        {versions.map(v => (
                            <option key={v.id} value={v.id}>{v.version === 0 ? 'Draft' : `v${v.version}`}</option>
                        ))}
                    </select>
                    <input className="uai-input uai-input-inline" placeholder="Why (optional)"
                        value={pinForm.notes} onChange={(e) => setPinForm(p => ({ ...p, notes: e.target.value }))} />
                    <Button
                        variant="secondary"
                        disabled={busy || !pinForm.uapi_access_id || !pinForm.version_id}
                        onClick={() => run(
                            () => uapiIntegrationsService.pin(id, {
                                uapi_access_id: Number(pinForm.uapi_access_id),
                                version_id: Number(pinForm.version_id),
                                notes: pinForm.notes || null,
                            }),
                            async () => { setPinForm({ uapi_access_id: '', version_id: '', notes: '' }); await loadIntegration(); })}
                    >
                        <Plus size={14} /> Pin
                    </Button>
                </div>
                <p className="uai-hint">
                    Pinning the DRAFT means that property runs whatever is saved, changing under it with every
                    save — right while iterating, wrong to leave in place.
                </p>
            </div>

            {/* VERSIONS, AND THE ONE CONTROL THAT CHANGES WHAT EVERY PROPERTY RUNS.
                SHARED with the pipeline template screen (VersionsTable). This table WAS written here,
                and the second screen that needed it made it shared rather than copied — the parts that
                get done differently every time they are done twice are exactly the ones here: whether
                "live" is a badge or a column, whether publishing also goes live, and whether the live
                version can be deleted. What varies between the two callers is only what a version
                CONTAINS, which is one column. */}
            <VersionsTable
                versions={versions}
                liveVersionId={integration.live_version_id}
                sizeLabel="Files"
                sizeOf={(v) => v.file_count}
                busy={busy}
                onMakeLive={(v) => setDialog({ kind: 'golive', version: v })}
                onTakeOffline={() => setDialog({ kind: 'offline' })}
                onDelete={(v) => setDialog({ kind: 'delversion', version: v })}
            />

            {/* ── dialogs ─────────────────────────────────────────────────── */}

            {dialog?.kind === 'newfile' && (
                <ConfirmModal
                    title="New file"
                    confirmLabel="Create"
                    onCancel={() => { setDialog(null); setNewPath(''); }}
                    onConfirm={() => run(
                        () => uapiIntegrationsService.saveFile(id, newPath.trim(), ''),
                        async () => { await loadFiles(null); openFile(newPath.trim()); setNewPath(''); })}
                >
                    <div className="uai-form">
                        <label className="uai-label">Path</label>
                        <input className="uai-input" value={newPath} autoFocus
                            onChange={(e) => setNewPath(e.target.value)} placeholder="lib/auth.js" />
                        <p className="uai-hint">Relative to the integration root. Folders exist by having slashes in the path.</p>
                    </div>
                </ConfirmModal>
            )}

            {dialog?.kind === 'delfile' && (
                <ConfirmModal
                    title={`Delete ${dialog.path}?`}
                    message="This removes the file from the draft. Published versions keep their own copy."
                    confirmLabel="Delete" danger
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(
                        () => uapiIntegrationsService.deleteFile(id, dialog.path),
                        async () => {
                            const list = await loadFiles(null);
                            if (openPath === dialog.path) {
                                const next = list[0];
                                if (next) openFile(next.path); else { setOpenPath(null); setContent(''); }
                            }
                        })}
                />
            )}

            {dialog?.kind === 'importzip' && (
                <ConfirmModal
                    title={`Import ${dialog.name}?`}
                    confirmLabel="Import into draft"
                    danger={dialog.removed.length > 0}
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(
                        () => uapiIntegrationsService.putTree(id, dialog.tree, 'replace'),
                        async () => {
                            setCheck(null);   // a new tree; the last compile result no longer describes it
                            const list = await loadFiles(null);
                            const next = list.find(f => f.path === openPath)
                                || list.find(f => f.path === integration.entry_path)
                                || list[0];
                            if (next) openFile(next.path, null);
                            else { setOpenPath(null); setContent(''); }
                        })}
                >
                    <div className="uai-form">
                        <p className="uai-hint">
                            {Object.keys(dialog.tree).length} file{Object.keys(dialog.tree).length === 1 ? '' : 's'} into
                            the draft. Published versions are untouched.
                        </p>
                        {dialog.removed.length > 0 && (
                            <>
                                <label className="uai-label">
                                    {dialog.removed.length} file{dialog.removed.length === 1 ? '' : 's'} will be deleted
                                </label>
                                {/* Named, not counted. "3 files will be deleted" is not something anyone
                                    can agree to — a zip made from a stale checkout looks identical to a
                                    correct one until you read which files are missing from it. */}
                                <ul className="uai-import-removed">
                                    {dialog.removed.map(p => <li key={p}><code>{p}</code></li>)}
                                </ul>
                            </>
                        )}
                        {!Object.prototype.hasOwnProperty.call(dialog.tree, integration.entry_path) && (
                            <p className="uai-hint uai-hint-warn">
                                This zip has no <code>{integration.entry_path}</code>, the entry point — the server
                                will refuse it. Change the entry point first, or add the file to the zip.
                            </p>
                        )}
                    </div>
                </ConfirmModal>
            )}

            {dialog?.kind === 'publish' && (
                <ConfirmModal
                    title="Publish the draft"
                    message="Freezes the draft as the next version. It does NOT go live — make it live, or pin a property to it, afterwards."
                    confirmLabel="Publish"
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(
                        () => uapiIntegrationsService.publish(id, dialog.notes || null),
                        loadIntegration)}
                >
                    <div className="uai-form">
                        <label className="uai-label">Notes</label>
                        <input className="uai-input" placeholder="What changed" value={dialog.notes}
                            onChange={(e) => setDialog(d => ({ ...d, notes: e.target.value }))} />
                        <p className="uai-hint">The draft is compiled first — a version that cannot load is never published.</p>
                    </div>
                </ConfirmModal>
            )}

            {dialog?.kind === 'golive' && (
                <ConfirmModal
                    title={`Make v${dialog.version.version} live?`}
                    message={`Every property on ${integration.system_name} starts running this version on its next call, except any pinned to a candidate. Rolling back is the same action on an older version.`}
                    confirmLabel="Make live"
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(() => uapiIntegrationsService.goLive(id, dialog.version.id), loadIntegration)}
                />
            )}

            {dialog?.kind === 'offline' && (
                <ConfirmModal
                    title="Take this integration offline?"
                    message="With no live version the Universal API answers 501 for this system — the same as before any integration existed. Properties pinned to a candidate keep running theirs."
                    confirmLabel="Take offline" danger
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(() => uapiIntegrationsService.takeOffline(id), loadIntegration)}
                />
            )}

            {dialog?.kind === 'delversion' && (
                <ConfirmModal
                    title={`Delete v${dialog.version.version}?`}
                    message="The snapshot and its files are removed. Any property pinned to it loses the pin and falls back to the live version."
                    confirmLabel="Delete" danger
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(() => uapiIntegrationsService.deleteVersion(id, dialog.version.id), loadIntegration)}
                />
            )}

            {dialog?.kind === 'restore' && (
                <ConfirmModal
                    title="Restore this version into the draft?"
                    message="The draft's current files are REPLACED by this version's. Anything unsaved in the draft is lost."
                    confirmLabel="Restore" danger
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(
                        () => uapiIntegrationsService.restoreVersion(id, viewingVersion),
                        async () => { setViewingVersion(null); await loadIntegration(); })}
                />
            )}

            {dialog?.kind === 'unpin' && (
                <ConfirmModal
                    title="Remove this pin?"
                    message={`${dialog.candidate.hotelname || 'That property'} goes back to the live version on its next call.`}
                    confirmLabel="Unpin" danger
                    onCancel={() => setDialog(null)}
                    onConfirm={() => run(() => uapiIntegrationsService.unpin(id, dialog.candidate.id), loadIntegration)}
                />
            )}
        </div>
    );
}

export default UapiIntegrationDetailPage;
